// FILE: src/components/admin/JourneyActivationScreen.js
//
// Admin → Journey Activation. The activation model asks one question: does a
// deliberate nudge turn a registered-but-dormant learner into one who has
// answered a question? This screen answers it per intervention.
//
// Intervention #1 is the inactive-24h lifecycle email. The funnel is measured
// per email JOB, relative to that job's own send time, inside an attribution
// window (7 days by default):
//
//   eligible → targeted → sent → delivered → clicked → returned → answered
//
// "clicked" is strictly link-attributed (the CTA carries the job id). Returns
// are measured from `authenticated_open` — emitted once per app session after
// identity resolves — and are split two ways: ANY authenticated return in the
// window, and the email-attributed subset that followed a click on that job.
// Clicked and returned are shown separately because either one alone misleads.
//
// Until migration 093 `returned` was derived from `app_open`, which carries a
// NULL user_id on 100% of rows, so it read zero for every cohort ever measured.
// Cohorts sent before the instrument existed show the sign-in proxy instead,
// explicitly labelled a lower bound — sign-in misses every learner who returned
// with a live session, which is most nudge recipients.
//
// Three things on this screen exist to stop an operator drawing a wrong
// conclusion, and should not be removed without replacing them:
//
//   1. The headline rate is matured_answered_first / MATURED_TARGETED. Targeted
//      (not delivered) matches how the natural baseline was constructed — over
//      `delivered` it would quietly exclude the learners a delivery failure
//      removed, flattering the intervention exactly when it is doing worst.
//      Matured (not everyone) keeps learners who are still inside their window
//      out of the denominator; they have not failed to activate, they have not
//      finished. Both denominators are load-bearing.
//   2. Every intervention shows its flag state explicitly. A held intervention
//      and a broken one both render as a funnel of zeros; only the flag tells
//      them apart.
//   3. The eligibility scheduler's heartbeat is shown here, because a stopped
//      sweep does not raise an error anywhere — it just makes volume fall.
//
// Read-only. Delivery health (bounces, retries, failures by template) stays in
// Admin → Email. Server enforces admin access.

import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchActivationIntervention,
  fetchSchedulerHealth,
  compareToBaseline,
  activationRate,
  operationalActivationRate,
  returnsPredateInstrument,
  INTERVENTIONS,
  NATURAL_ACTIVATION_BASELINE,
  MIN_COHORT_FOR_READ,
  RETURN_SIGNAL,
  rate,
} from '../../services/activation/activationService';

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const S = {
  root: { padding: '20px', maxWidth: 1000, margin: '0 auto', fontFamily: '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif', color: '#1a2233' },
  bar: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0 18px' },
  back: { background: 'transparent', border: '1px solid #ccd4e6', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#1a2233' },
  input: { padding: '7px 9px', border: '1px solid #ccd4e6', borderRadius: 8, fontSize: 14 },
  h1: { fontSize: 20, margin: '0 0 4px' },
  sub: { color: '#6b7280', fontSize: 13, margin: 0 },
  blurb: { color: '#6b7280', fontSize: 13, margin: '0 0 6px' },
  scope: { color: '#6b7280', fontSize: 12, margin: '0 0 14px', lineHeight: 1.5 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 },
  card: { background: '#fff', border: '1px solid #e6eaf2', borderRadius: 12, padding: '14px 16px' },
  cardH: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6b7280', fontWeight: 800, marginBottom: 6 },
  big: { fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 },
  note: { fontSize: 12, color: '#6b7280', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', border: '1px solid #e6eaf2', borderRadius: 12, overflow: 'hidden' },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6b7280', fontWeight: 800, padding: '10px 12px', borderBottom: '1px solid #eef1f7' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f4fa' },
  num: { fontVariantNumeric: 'tabular-nums', fontWeight: 700, textAlign: 'right' },
  h2: { fontSize: 15, margin: '20px 0 8px' },
  warn: { background: '#fff8e6', border: '1px solid #ffe2a8', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 16 },
  off: { background: '#eef2f9', border: '1px solid #d6deec', borderRadius: 10, padding: '10px 12px', fontSize: 13, marginBottom: 16 },
  pill: { display: 'inline-block', fontSize: 11, fontWeight: 800, letterSpacing: '.4px', textTransform: 'uppercase', borderRadius: 999, padding: '3px 9px', marginLeft: 8, verticalAlign: 'middle' },
  pillOn: { background: '#e6f6ec', color: '#1c7a43', border: '1px solid #b9e4c9' },
  pillOff: { background: '#eceff5', color: '#5b6579', border: '1px solid #d6deec' },
  health: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderRadius: 10, padding: '9px 12px', marginBottom: 16, border: '1px solid' },
  dot: { width: 8, height: 8, borderRadius: 999, flex: '0 0 auto' },
};

const HEALTH_STYLE = {
  healthy:  { background: '#f2fbf5', borderColor: '#c9e9d5', color: '#1c7a43', dot: '#2e9e5b', label: 'Healthy' },
  warning:  { background: '#fff8e6', borderColor: '#ffe2a8', color: '#8a5d00', dot: '#e0a010', label: 'Warning' },
  critical: { background: '#fdf0f0', borderColor: '#f5c9c9', color: '#a12626', dot: '#d33', label: 'Critical' },
  unknown:  { background: '#fff8e6', borderColor: '#ffe2a8', color: '#8a5d00', dot: '#e0a010', label: 'Unknown' },
};

const FUNNEL = [
  { key: 'targeted',        label: 'Targeted (enqueued)', of: null,       hint: 'Reminder jobs created — the cohort denominator' },
  { key: 'sent',            label: 'Sent',                of: 'targeted', hint: 'Handed to Resend' },
  { key: 'delivered',       label: 'Delivered',           of: 'sent',     hint: 'Confirmed by webhook' },
  { key: 'clicked',         label: 'Clicked the CTA',     of: 'delivered', hint: 'Link attribution — job id carried in the CTA' },
  { key: 'returned_authenticated',    label: 'Returned (authenticated)', of: 'delivered', hint: `Any ${RETURN_SIGNAL.event} inside the window, however they arrived` },
  { key: 'returned_email_attributed', label: '…of which email-attributed', of: 'returned_authenticated', hint: 'Returned at or after a click on this email — a subset of the row above' },
  { key: 'answered_first',  label: 'Answered a question', of: 'targeted', hint: 'All sends, including windows still open — operational, not the efficacy figure' },
];

// The efficacy cohort. Kept in its own table so a still-accruing operational
// funnel can never be mistaken for the experimental result.
const MATURED = [
  { key: 'matured_targeted',       label: 'Matured targeted',   of: null,               hint: 'Attribution window has fully closed' },
  { key: 'in_flight',              label: 'Still in flight',    of: null,               hint: 'Window still open — not yet scoreable, NOT a non-activation' },
  { key: 'matured_answered_first', label: 'Matured activated',  of: 'matured_targeted', hint: 'Answered a first question inside their closed window' },
  { key: 'answered_first_late',    label: 'Answered late (descriptive)', of: 'targeted', hint: 'First answer in the window AFTER the attribution window — observation only, not efficacy' },
];

const HEALTH_ROWS = [
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'bounced', label: 'Bounced' },
  { key: 'complained', label: 'Complained' },
  { key: 'pending', label: 'Still pending' },
  { key: 'failed', label: 'Failed' },
];

function fmtPct(v) { return v === null || v === undefined ? '—' : `${v}%`; }
function fmtHours(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n < 1) return `${Math.round(n * 60)} min`;
  if (n < 48) return `${n} h`;
  return `${Math.round((n / 24) * 10) / 10} days`;
}
function fmtAge(hours) {
  if (hours === null || hours === undefined) return 'never';
  const n = Number(hours);
  if (!Number.isFinite(n)) return 'never';
  if (n < 1) return `${Math.round(n * 60)}m ago`;
  if (n < 48) return `${Math.round(n)}h ago`;
  return `${Math.round((n / 24) * 10) / 10} days ago`;
}

export default function JourneyActivationScreen({ onBack }) {
  const [journey, setJourney] = useState('inactive_24h');
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [windowDays, setWindowDays] = useState(7);
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [res, sched] = await Promise.all([
      fetchActivationIntervention({ journey, from, to, windowDays }),
      fetchSchedulerHealth('prod'),
    ]);
    setData(res);
    setHealth(sched);
    setLoading(false);
  }, [journey, from, to, windowDays]);

  useEffect(() => { load(); }, [load]);

  const meta = INTERVENTIONS.find((i) => i.key === journey) || INTERVENTIONS[0];
  const targeted = data ? Number(data.targeted || 0) : 0;
  const enabled = data ? data.enabled === true : null;
  const cmp = compareToBaseline(data);
  const hs = health ? (HEALTH_STYLE[health.status] || HEALTH_STYLE.unknown) : null;

  return (
    <div style={S.root} data-testid="journey-activation-screen">
      <div style={S.bar}>
        {onBack && (
          <button type="button" style={S.back} onClick={onBack} data-testid="activation-back">← Back</button>
        )}
        <div>
          <h1 style={S.h1}>Journey Activation</h1>
          <p style={S.sub}>Do our nudges turn dormant registrations into answering learners?</p>
        </div>
      </div>

      <div style={S.bar}>
        <select
          style={S.input}
          value={journey}
          onChange={(e) => setJourney(e.target.value)}
          data-testid="activation-journey"
          aria-label="Intervention"
        >
          {INTERVENTIONS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
        </select>
        <input style={S.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <input style={S.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
        <select
          style={S.input}
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          aria-label="Attribution window"
        >
          {[3, 7, 14, 30].map((d) => <option key={d} value={d}>{d}-day window</option>)}
        </select>
        <button type="button" style={S.back} onClick={load} data-testid="activation-refresh">Refresh</button>
      </div>

      {/* A stopped sweep raises no error anywhere — it just makes volume fall.
          Shown first so it is read before the numbers it would explain. */}
      {health && hs && (
        <div
          style={{ ...S.health, background: hs.background, borderColor: hs.borderColor, color: hs.color }}
          data-testid="activation-scheduler-health"
        >
          <span style={{ ...S.dot, background: hs.dot }} />
          <span>
            <strong>Eligibility Scheduler: {hs.label}</strong>
            {' · '}
            {health.status === 'unknown'
              ? 'No successful production sweep on record yet'
              : `Prod last success ${fmtAge(health.hours_since_success)}`}
            {health.status === 'critical' && ' — registrations are ageing toward the 4-day eligibility cutoff'}
            {health.last_run_success === false && health.last_run_error
              ? ` · last run failed: ${String(health.last_run_error).slice(0, 120)}`
              : ''}
          </span>
        </div>
      )}

      <p style={S.blurb}>
        {meta.blurb}
        {enabled !== null && (
          <span
            style={{ ...S.pill, ...(enabled ? S.pillOn : S.pillOff) }}
            data-testid="activation-flag-state"
          >
            {enabled ? 'On' : 'Off'}
          </span>
        )}
      </p>

      <p style={S.scope} data-testid="activation-scope">
        Cohort: <strong>{meta.label}</strong> jobs created {from} → {to}
        {targeted > 0 ? ` (${targeted} learner${targeted === 1 ? '' : 's'})` : ''}.
        Outcomes are counted per learner for {data?.window_days ?? windowDays} days after
        their own send, so a job near the end of the range may still be accruing.
      </p>

      {loading && <p data-testid="activation-loading">Loading…</p>}

      {!loading && !data && (
        <p data-testid="activation-empty">No activation data available.</p>
      )}

      {!loading && data && (
        <>
          {/* "Switched off" and "switched on but producing nothing" are the same
              funnel of zeros. Say which one this is. */}
          {enabled === false && (
            <div style={S.off} data-testid="activation-disabled">
              <strong>This intervention is switched off.</strong>{' '}
              {data.master_enabled === false
                ? 'The master flag inactive_registration_enabled is false, so no inactive reminder of any kind is being sent.'
                : `The per-window flag ${journey}_enabled is false.`}{' '}
              The zeros below are a decision, not a fault
              {Number(data.eligible_now || 0) > 0
                ? ` — ${data.eligible_now} learner${Number(data.eligible_now) === 1 ? '' : 's'} would qualify if it were turned on.`
                : '.'}
            </div>
          )}

          {enabled === true && targeted === 0 && (
            <div style={S.warn} data-testid="activation-not-started">
              This intervention is on but has not enqueued anything in the selected range.
              Check the eligibility scheduler status above before assuming there was simply
              nobody to remind.
            </div>
          )}

          <div style={S.cards}>
            <div style={S.card}>
              <div style={S.cardH}>Eligible right now</div>
              <div style={S.big} data-testid="activation-eligible-now">{data.eligible_now ?? '—'}</div>
              <div style={S.note}>
                {enabled === false ? 'Would qualify if switched on' : "Would qualify on today's sweep"}
              </div>
            </div>
            <div style={S.card}>
              <div style={S.cardH}>Activation rate (matured)</div>
              <div style={S.big} data-testid="activation-rate">{fmtPct(activationRate(data))}</div>
              <div style={S.note} data-testid="activation-rate-note">
                {data.matured_answered_first ?? 0} of {data.matured_targeted ?? 0} matured learners
                answered a first question
                {Number(data.in_flight || 0) > 0
                  ? ` · ${data.in_flight} still in flight, excluded`
                  : ''}
                {' · '}operational {fmtPct(operationalActivationRate(data))} over all {targeted}
              </div>
            </div>
            <div style={S.card}>
              <div style={S.cardH}>vs natural baseline</div>
              <div style={S.big} data-testid="activation-baseline-delta">
                {cmp.deltaPts === null
                  ? '—'
                  : `${cmp.deltaPts > 0 ? '+' : ''}${cmp.deltaPts} pts`}
              </div>
              <div style={S.note} data-testid="activation-baseline-note">
                {cmp.verdict === 'too-early'
                  ? `Baseline ${cmp.baselinePct}% · not a result yet (${cmp.cohort}/${MIN_COHORT_FOR_READ} matured)`
                  : `Baseline ${cmp.baselinePct}% — dormant learners who self-activate unprompted (${NATURAL_ACTIVATION_BASELINE.numerator}/${NATURAL_ACTIVATION_BASELINE.denominator}, measured ${NATURAL_ACTIVATION_BASELINE.measuredOn})`}
              </div>
            </div>
            <div style={S.card}>
              <div style={S.cardH}>Median time to first question</div>
              <div style={S.big}>{fmtHours(data.hours_to_first_median)}</div>
              <div style={S.note}>Average {fmtHours(data.hours_to_first_avg)}</div>
            </div>
          </div>

          <h2 style={S.h2}>Funnel</h2>
          <table style={S.table} data-testid="activation-funnel">
            <thead>
              <tr>
                <th style={S.th}>Step</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Count</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Rate</th>
                <th style={S.th}>Basis</th>
              </tr>
            </thead>
            <tbody>
              {FUNNEL.map((row) => (
                <tr key={row.key}>
                  <td style={S.td}>{row.label}</td>
                  <td style={{ ...S.td, ...S.num }} data-testid={`activation-${row.key}`}>{data[row.key] ?? 0}</td>
                  <td style={{ ...S.td, ...S.num }}>{row.of ? fmtPct(rate(data[row.key], data[row.of])) : '—'}</td>
                  <td style={{ ...S.td, color: '#6b7280' }}>{row.hint}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* A cohort predating the return instrument reads zero authenticated
              returns no matter what the learners did. Say so, and show the
              lower-bound proxy rather than a number that looks like behaviour. */}
          {returnsPredateInstrument(data) && (
            <div style={S.warn} data-testid="activation-return-legacy">
              <strong>Return figures under-read for this cohort.</strong>{' '}
              {data.instrumentation?.sends_predating_fix ?? 0} of {targeted} send
              {targeted === 1 ? '' : 's'} predate the <code>{RETURN_SIGNAL.event}</code> instrument
              (migration {RETURN_SIGNAL.fixedInMigration}), so an authenticated return could not
              have been recorded for them. The earlier signal, <code>{RETURN_SIGNAL.legacyEvent}</code>,
              carries a NULL user id on every row and never attributed anything.{' '}
              <strong>
                Legacy lower bound: {data.returned_signin_legacy ?? 0} of {targeted} signed in
                inside their window
              </strong>{' '}
              — a floor, not the return rate, because it misses everyone who came back on a live
              session without re-authenticating.
            </div>
          )}

          <h2 style={S.h2}>Efficacy cohort (matured only)</h2>
          <table style={S.table} data-testid="activation-matured">
            <thead>
              <tr>
                <th style={S.th}>Step</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Count</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Rate</th>
                <th style={S.th}>Basis</th>
              </tr>
            </thead>
            <tbody>
              {MATURED.map((row) => (
                <tr key={row.key}>
                  <td style={S.td}>{row.label}</td>
                  <td style={{ ...S.td, ...S.num }} data-testid={`activation-${row.key}`}>{data[row.key] ?? 0}</td>
                  <td style={{ ...S.td, ...S.num }}>{row.of ? fmtPct(rate(data[row.key], data[row.of])) : '—'}</td>
                  <td style={{ ...S.td, color: '#6b7280' }}>{row.hint}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 style={S.h2}>Deliverability + opt-outs</h2>
          <table style={S.table} data-testid="activation-health">
            <tbody>
              {HEALTH_ROWS.map((row) => (
                <tr key={row.key}>
                  <td style={S.td}>{row.label}</td>
                  <td style={{ ...S.td, ...S.num }} data-testid={`activation-${row.key}`}>{data[row.key] ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
